import type { AssistantUsage } from './assistantTokenUsage';
import type {
  AssistantMessage,
  AssistantMessageBlock,
  AssistantMessageRole,
  AssistantStepEvent,
  AssistantSummaryEvent,
  AssistantToolCall,
} from './assistantTypes';
import type {
  GatewayChangedFile,
  GatewayTranscriptMessage,
} from '@/app/sdk';

export function normaliseAssistantTranscriptMessages(transcript: GatewayTranscriptMessage[]): AssistantMessage[] {
  return transcript
    .map((message, index) => normaliseAssistantTranscriptMessage(message, `restored-transcript-${index}`))
    .filter((message): message is AssistantMessage => !!message);
}


function normaliseAssistantTranscriptMessage(value: GatewayTranscriptMessage, fallbackId: string): AssistantMessage | null {
  if (!isRecord(value)) {
    return null;
  }

  const role = normaliseAssistantMessageRole(value['role']);
  if (!role) {
    return null;
  }

  const id = readTrimmedString(value, 'id') ?? fallbackId;
  const tools = normaliseAssistantTools(value['tools']);
  const blocks = reconcileAssistantToolBlocks(normaliseAssistantMessageBlocks(value['blocks']), tools);
  return {
    id,
    role,
    content: typeof value['content'] === 'string' ? value['content'] : '',
    runId: readFiniteNumber(value, 'runId'),
    thinking: readOptionalString(value, 'thinking'),
    thinkingDurationMs: readFiniteNumber(value, 'thinkingDurationMs'),
    tokenProgress: value['tokenProgress'],
    steps: normaliseAssistantSteps(value['steps']),
    summaries: normaliseAssistantSummaries(value['summaries']),
    shellOutputs: Array.isArray(value['shellOutputs']) ? [...value['shellOutputs']] : undefined,
    shellOutputDroppedCount: readFiniteNumber(value, 'shellOutputDroppedCount'),
    lastUserMessageAppended: value['lastUserMessageAppended'],
    usage: normaliseAssistantUsage(value['usage']),
    tools,
    blocks,
    changedFiles: normaliseAssistantChangedFiles(value['changedFiles']),
  };
}


function normaliseAssistantMessageRole(value: unknown): AssistantMessageRole | null {
  return value === 'user' || value === 'assistant' || value === 'system' ? value : null;
}

function normaliseAssistantSteps(value: unknown): AssistantStepEvent[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const steps: AssistantStepEvent[] = [];
  for (const step of value) {
    if (!isRecord(step) || (step['status'] !== 'started' && step['status'] !== 'completed')) {
      continue;
    }
    steps.push({
      status: step['status'],
      stepId: readOptionalString(step, 'stepId'),
      stepDurationMs: readFiniteNumber(step, 'stepDurationMs'),
      step: step['step'],
    });
  }
  return steps.length > 0 ? steps : undefined;
}

function normaliseAssistantSummaries(value: unknown): AssistantSummaryEvent[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const summaries: AssistantSummaryEvent[] = [];
  for (const summary of value) {
    if (
      !isRecord(summary)
      || (summary['status'] !== 'started' && summary['status'] !== 'delta' && summary['status'] !== 'completed')
    ) {
      continue;
    }
    summaries.push({
      status: summary['status'],
      summary: readOptionalString(summary, 'summary'),
    });
  }
  return summaries.length > 0 ? summaries : undefined;
}

function normaliseAssistantUsage(value: unknown): AssistantUsage | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const usage: AssistantUsage = {
    inputTokens: readFiniteNumber(value, 'inputTokens'),
    outputTokens: readFiniteNumber(value, 'outputTokens'),
    cacheReadTokens: readFiniteNumber(value, 'cacheReadTokens'),
    cacheWriteTokens: readFiniteNumber(value, 'cacheWriteTokens'),
  };
  return Object.values(usage).some(tokenCount => tokenCount !== undefined) ? usage : undefined;
}

function normaliseAssistantMessageBlocks(value: unknown): AssistantMessageBlock[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const blocks: AssistantMessageBlock[] = [];
  for (const block of value) {
    if (!isRecord(block) || typeof block['type'] !== 'string') {
      continue;
    }
    if (block['type'] === 'text' && typeof block['content'] === 'string') {
      blocks.push({ type: 'text', content: block['content'] });
      continue;
    }
    if (block['type'] === 'tool' && typeof block['toolId'] === 'string' && block['toolId'].trim()) {
      blocks.push({ type: 'tool', toolId: block['toolId'].trim() });
    }
  }
  return blocks.length > 0 ? blocks : undefined;
}

function normaliseAssistantTools(value: unknown): AssistantToolCall[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const tools: AssistantToolCall[] = [];
  for (const tool of value) {
    if (!isRecord(tool)) {
      continue;
    }
    const id = readTrimmedString(tool, 'id');
    const name = readTrimmedString(tool, 'name');
    const status = tool['status'];
    if (!id || !name || (status !== 'running' && status !== 'completed' && status !== 'error')) {
      continue;
    }
    tools.push({
      id,
      name,
      status,
      args: tool['args'],
      result: tool['result'],
    });
  }
  return tools.length > 0 ? tools : undefined;
}

function reconcileAssistantToolBlocks(
  blocks: AssistantMessageBlock[] | undefined,
  tools: AssistantToolCall[] | undefined,
): AssistantMessageBlock[] | undefined {
  if (!blocks?.length) {
    if (!tools?.length) {
      return undefined;
    }
    return tools.map(tool => ({ type: 'tool', toolId: tool.id }));
  }

  const validToolIds = new Set((tools ?? []).map(tool => tool.id));
  const seenToolIds = new Set<string>();
  const nextBlocks: AssistantMessageBlock[] = [];
  for (const block of blocks) {
    if (block.type === 'text') {
      nextBlocks.push(block);
      continue;
    }
    if (!validToolIds.has(block.toolId) || seenToolIds.has(block.toolId)) {
      continue;
    }
    seenToolIds.add(block.toolId);
    nextBlocks.push(block);
  }

  for (const tool of tools ?? []) {
    if (seenToolIds.has(tool.id)) {
      continue;
    }
    nextBlocks.push({ type: 'tool', toolId: tool.id });
  }

  return nextBlocks.length > 0 ? nextBlocks : undefined;
}

function normaliseAssistantChangedFiles(value: unknown): GatewayChangedFile[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const files: GatewayChangedFile[] = [];
  for (const file of value) {
    if (!isRecord(file)) {
      continue;
    }
    const path = readTrimmedString(file, 'path');
    if (!path) {
      continue;
    }
    files.push({
      path,
      status: readOptionalString(file, 'status'),
    });
  }
  return files.length > 0 ? files : undefined;
}

function readTrimmedString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function readOptionalString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
}

function readFiniteNumber(record: Record<string, unknown>, key: string): number | undefined {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
